// ScheduleFlightModal.jsx - Schedule a flight for automatic dispatch at a specific time

import React, { useState, useMemo, useCallback } from "react";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { AIRCRAFT_DATABASE } from "../../data/aircraft.js";
import { Button } from "@/components/ui/button";
import { debug } from "../../lib/debug.js";

export default function ScheduleFlightModal({ isOpen, onClose, preselectedRoute }) {
  const { worldState, addScheduledFlight } = useWorldState();

  const [selectedRouteId, setSelectedRouteId] = useState(preselectedRoute?.id || "");
  const [selectedAircraftId, setSelectedAircraftId] = useState("next-available");
  const [departureHour, setDepartureHour] = useState("06");
  const [departureMinute, setDepartureMinute] = useState("00");
  const [recurring, setRecurring] = useState(false);

  // Reset form when modal opens with new preselected route
  const resetForm = useCallback(() => {
    setSelectedRouteId(preselectedRoute?.id || "");
    setSelectedAircraftId(preselectedRoute?.aircraftId || "next-available");
    setDepartureHour("06");
    setDepartureMinute("00");
    setRecurring(false);
  }, [preselectedRoute]);

  // Available routes (active, with origin/destination)
  const availableRoutes = useMemo(() => {
    return (worldState?.routes || []).filter(r => r.origin && r.destination && r.active !== false);
  }, [worldState?.routes]);

  // Available aircraft (not currently in flight)
  const availableAircraft = useMemo(() => {
    const inFlightIds = new Set(
      (worldState?.flights || [])
        .filter(f => f.status !== 'completed' && f.status !== 'cancelled')
        .map(f => f.aircraftId)
    );
    return (worldState?.aircraft || []).filter(a => !inFlightIds.has(a.id) && a.status !== 'in_flight');
  }, [worldState?.aircraft, worldState?.flights]);

  // Get selected route info
  const selectedRoute = useMemo(() => {
    return availableRoutes.find(r => r.id === selectedRouteId) || null;
  }, [availableRoutes, selectedRouteId]);

  const handleConfirm = useCallback(() => {
    if (!selectedRouteId) return;

    const time = `${departureHour.padStart(2, '0')}:${departureMinute.padStart(2, '0')}`;

    addScheduledFlight({
      routeId: selectedRouteId,
      aircraftId: selectedAircraftId,
      departureTime: time,
      recurring,
      autoReturn: false,
    });

    debug('[ScheduleFlight] Created schedule:', { routeId: selectedRouteId, time, recurring, aircraftId: selectedAircraftId });
    onClose();
  }, [selectedRouteId, selectedAircraftId, departureHour, departureMinute, recurring, addScheduledFlight, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-zinc-900 rounded-lg p-6 max-w-md w-full mx-4"
        style={{ border: "1px solid #333" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <h2 className="text-xl font-bold mb-4" style={{ color: "#fff" }}>
          Schedule Flight
        </h2>

        {/* Route Selection */}
        <div className="mb-4">
          <label className="block text-sm mb-1" style={{ color: "#737373" }}>Route</label>
          <select
            value={selectedRouteId}
            onChange={(e) => setSelectedRouteId(e.target.value)}
            className="w-full rounded px-3 py-2 text-sm"
            style={{ backgroundColor: "#1a1a1a", color: "#fff", border: "1px solid #333" }}
          >
            <option value="">Select a route...</option>
            {availableRoutes.map(route => (
              <option key={route.id} value={route.id}>
                {route.currentLegOrigin || route.origin} → {route.currentLegDestination || route.destination}
              </option>
            ))}
          </select>
        </div>

        {/* Aircraft Selection */}
        <div className="mb-4">
          <label className="block text-sm mb-1" style={{ color: "#737373" }}>Aircraft</label>
          <select
            value={selectedAircraftId}
            onChange={(e) => setSelectedAircraftId(e.target.value)}
            className="w-full rounded px-3 py-2 text-sm"
            style={{ backgroundColor: "#1a1a1a", color: "#fff", border: "1px solid #333" }}
          >
            <option value="next-available">Next Available</option>
            {availableAircraft.map(ac => {
              const spec = AIRCRAFT_DATABASE[ac.type || ac.model] || {};
              return (
                <option key={ac.id} value={ac.id}>
                  {ac.registration || ac.id} — {spec.model || ac.type || ac.model}
                </option>
              );
            })}
          </select>
        </div>

        {/* Time Picker */}
        <div className="mb-4">
          <label className="block text-sm mb-1" style={{ color: "#737373" }}>Departure Time (Game Time)</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="23"
              value={departureHour}
              onChange={(e) => setDepartureHour(Math.min(23, Math.max(0, parseInt(e.target.value) || 0)).toString())}
              className="w-16 rounded px-3 py-2 text-sm text-center"
              style={{ backgroundColor: "#1a1a1a", color: "#fff", border: "1px solid #333" }}
            />
            <span className="text-lg font-bold" style={{ color: "#fff" }}>:</span>
            <input
              type="number"
              min="0"
              max="59"
              value={departureMinute}
              onChange={(e) => setDepartureMinute(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)).toString())}
              className="w-16 rounded px-3 py-2 text-sm text-center"
              style={{ backgroundColor: "#1a1a1a", color: "#fff", border: "1px solid #333" }}
            />
            <span className="text-sm" style={{ color: "#525252" }}>
              ({departureHour.padStart(2, '0')}:{departureMinute.padStart(2, '0')})
            </span>
          </div>
        </div>

        {/* Frequency Toggle */}
        <div className="mb-6">
          <label className="block text-sm mb-2" style={{ color: "#737373" }}>Frequency</label>
          <div className="flex gap-2">
            <button
              onClick={() => setRecurring(false)}
              className="flex-1 py-2 rounded text-sm font-bold"
              style={{
                backgroundColor: !recurring ? "#3b82f6" : "#1a1a1a",
                color: !recurring ? "#fff" : "#525252",
                border: `1px solid ${!recurring ? "#3b82f6" : "#333"}`,
              }}
            >
              One-time
            </button>
            <button
              onClick={() => setRecurring(true)}
              className="flex-1 py-2 rounded text-sm font-bold"
              style={{
                backgroundColor: recurring ? "#8b5cf6" : "#1a1a1a",
                color: recurring ? "#fff" : "#525252",
                border: `1px solid ${recurring ? "#8b5cf6" : "#333"}`,
              }}
            >
              Daily
            </button>
          </div>
        </div>

        {/* Summary */}
        {selectedRoute && (
          <div
            className="p-3 rounded-lg mb-4"
            style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}
          >
            <div className="text-sm" style={{ color: "#a1a1a1" }}>
              <span style={{ color: "#fff", fontWeight: "bold" }}>
                {selectedRoute.currentLegOrigin || selectedRoute.origin} → {selectedRoute.currentLegDestination || selectedRoute.destination}
              </span>
              {" at "}
              <span style={{ color: "#22c55e", fontWeight: "bold" }}>
                {departureHour.padStart(2, '0')}:{departureMinute.padStart(2, '0')}
              </span>
              {recurring && <span style={{ color: "#8b5cf6" }}> (daily)</span>}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            style={{ borderColor: "#525252", color: "#a1a1a1" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedRouteId}
            style={{
              backgroundColor: selectedRouteId ? "#22c55e" : "#1a1a1a",
              color: selectedRouteId ? "#000" : "#525252",
              fontWeight: "bold",
            }}
          >
            Confirm Schedule
          </Button>
        </div>
      </div>
    </div>
  );
}
